import React, { useEffect, useRef, useState } from 'react';
import { Terminal, RefreshCw, Search } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../config';
import { readControlCenterResponse } from './controlCenterApi';

export default function RenderConsole({ children, target = 'admin' }) {
  const { getIdToken } = useAuth();
  const tokenRef = useRef(getIdToken);
  tokenRef.current = getIdToken;
  const [refresh, setRefresh] = useState(0);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [updated, setUpdated] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const terminalRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const previousTarget = useRef(target);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 25000);
    let active = true;
    if (previousTarget.current !== target) {
      previousTarget.current = target;
      stickToBottomRef.current = true;
      setLogs([]);
      setUpdated(null);
      setError('');
      setSearchInput('');
      setSearch('');
    }
    setLoading(true);
    async function load() {
      try {
        const token = await tokenRef.current();
        if (!active) return;
        const response = await fetch(`${API_BASE_URL}/api/control-center/logs?target=${target}`, {
          headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
        });
        const data = await readControlCenterResponse(response);
        if (!response.ok || !data.ok) throw new Error(data.error || 'Unable to load Render logs.');
        if (active) { setLogs(data.logs || []); setUpdated(data.fetchedAt); setError(''); }
      } catch (err) {
        if (active) setError(err.name === 'AbortError' ? 'Log request timed out. Try refreshing again.' : err.message);
      } finally { clearTimeout(timeout); if (active) setLoading(false); }
    }
    load();
    return () => { active = false; controller.abort(); clearTimeout(timeout); };
  }, [target, refresh]);

  useEffect(() => {
    if (loading || error) return undefined;
    const timer = setInterval(() => {
      if (!document.hidden) setRefresh(value => value + 1);
    }, 15000);
    return () => clearInterval(timer);
  }, [loading, error]);

  useEffect(() => {
    if (!search && stickToBottomRef.current && terminalRef.current) terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
  }, [logs, search]);

  function handleTerminalScroll(event) {
    const terminal = event.currentTarget;
    stickToBottomRef.current = terminal.scrollHeight - terminal.clientHeight - terminal.scrollTop <= 48;
  }

  function handleSearch(event) {
    event.preventDefault();
    const next = searchInput.trim();
    setSearch(next);
    if (terminalRef.current) terminalRef.current.scrollTop = 0;
    stickToBottomRef.current = !next;
  }

  const visible = logs.filter(log => log.message.toLowerCase().includes(search.toLowerCase()));
  const formatTime = value => value && !Number.isNaN(Date.parse(value))
    ? new Date(value).toLocaleString() : '—';

  return <section className="cc-card cc-console" aria-labelledby="cc-console-title">
    <div className="cc-card-heading">
      <div className="cc-heading-copy"><h2 id="cc-console-title">Runtime logs</h2><p>{target === 'admin' ? 'Admin Dashboard' : 'Mobile App'} output, warnings, and errors.</p></div>
      <div className="cc-console-actions">
        <form className="cc-search-field cc-console-search" role="search" onSubmit={handleSearch}><Search size={17} aria-hidden="true" /><input aria-label="Search logs" placeholder="Search logs..." value={searchInput} onChange={event => setSearchInput(event.target.value)} /></form>
        <button className="cc-button" disabled={loading} onClick={() => setRefresh(value => value + 1)}><RefreshCw size={16} className={loading ? 'cc-spinning' : ''} aria-hidden="true" /> Refresh</button>
      </div>
    </div>
    {children}
    {error && <div className="cc-message cc-error" role="alert">{error} Press Refresh to try again.{updated && ' Previously fetched logs remain below.'}</div>}
    <div className="cc-terminal-shell">
      <div className="cc-terminal-bar"><span><Terminal size={16} aria-hidden="true" />{target === 'admin' ? 'Admin Dashboard' : 'Mobile App'}<span className="cc-terminal-source"> / runtime</span></span>
        <span className={`cc-stream-status ${error ? 'cc-stream-error' : 'cc-stream-active'}`}><span className="cc-status-dot" />{loading ? 'Fetching…' : error ? 'Disconnected' : 'Auto-refresh · 15s'}</span>
      </div>
      <div className="cc-terminal" ref={terminalRef} role="region" aria-label="Render runtime logs" tabIndex={0} onScroll={handleTerminalScroll}>
      {!visible.length ? <div className="cc-terminal-empty"><Terminal size={28} strokeWidth={1.3} aria-hidden="true" /><p>{loading && !logs.length ? 'Connecting to Render…' : error && !logs.length ? 'Could not load logs. Try Refresh.' : logs.length ? 'No logs match your search.' : 'No application logs returned for the last hour.'}</p></div>
        : visible.map((log, index) => <div key={log.id || `${log.timestamp}-${index}`} className={`cc-log-line cc-log-${log.level}`}>
          <time>{formatTime(log.timestamp)}</time><span className="cc-log-level">{log.level}</span><pre>{log.message}</pre>
        </div>)}
      </div>
    </div>
  </section>;
}
